import {
  AreaCategory,
  UserProfile,
  UserWorkoutSession,
  WorkoutSettings,
  staticMachineAreaZones,
} from 'fitbeat.gym-core';
import {
  CardioOrResistance,
  EnabledFeatures, Gym, GymTypes, IEnabledFeatures,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import React from 'react';
import {
  Platform, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { getInset } from 'react-native-safe-area-view';
import { connect } from 'react-redux';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import build from '../../config/build';
import { IAppStore } from '../../HomeWorkouts';
import { GymResolver } from '../../services';
import { UserProfileService } from '../../services/UserProfileService';
import Theme from '../../theme';
import { StatusBarComponent } from '../../ui/components/StatusBar';
import { getGymTimezone } from '../../utils/gyms';
import { defaultScale } from '../../utils/scaling';
import { checkInOnDemandBookingPressedAction } from '../actions';
import {
  BookingFlowFooter,
  BookingOperationButtons,
  OpenClassList,
  UserBookingDisplay,
  WorkoutTypeWithBodyGroupText,
} from '../components';
import { BookingSummary } from '../components/BookingSummary';
import { TypeOfWorkout } from '../components/TypeOfWorkout';
import { WhatYouNeedForWokoutText } from '../components/WhatYouNeedForWokoutText';
import { WorkoutFormat } from '../components/WorkoutFormat';
import { WorkoutSettingsSummary } from '../components/WorkoutSettingsSummary';
import { minutesToAllowBookingsAfterClassStarted } from '../utils/BookingUtils';
import { LivestreamOptionsSummary } from './LivestreamOptionsSummary';
import SessionTimings from '../components/SessionTimings';

interface IProps {
  gym: Gym;
  workoutSession: UserWorkoutSession;
  workoutSettings: WorkoutSettings;
  isStudioPartnerBooking: boolean;
  userProfile: UserProfile;
  userProfileService: UserProfileService;
  enabledFeatures: IEnabledFeatures;
  isHomeGroupClass: boolean;
  showCusomiseWorkoutSettings: boolean;
  closeModal(): void;
  t(str: string): string;
  showCustomiseWorkout(): void;
  onWorkoutTypeSelected(cardioOrResistance: CardioOrResistance): void;
  sendInvite(): void;
  changeBooking(): void;
  cancelBooking(): void;
  changeInvite(): void;
  shareOpenInvite(): void;
}

interface IMapStateProps {
  canCheckIn: boolean;
  checkingInLoading: boolean;
}

interface IMapDispatchProps {
  checkInPressed(): void;
}

const { isElite } = build.variant;
const bottomInset = Platform.OS !== 'web' ? getInset('bottom', false) : 0;
const BookingDetails: React.FC<IProps & IMapDispatchProps & IMapStateProps> = ({
  t,
  gym,
  closeModal,
  workoutSettings,
  isStudioPartnerBooking,
  workoutSession,
  userProfileService,
  userProfile,
  showCustomiseWorkout,
  onWorkoutTypeSelected,
  enabledFeatures,
  isHomeGroupClass,
  sendInvite,
  showCusomiseWorkoutSettings,
  changeBooking,
  cancelBooking,
  changeInvite,
  shareOpenInvite,
  canCheckIn,
  checkInPressed,
  checkingInLoading,
}) => {
  const gymTimezone = getGymTimezone(workoutSession);
  const isHomeBooking = gym.type === GymTypes.Home;
  const startOfExerciseMoment = moment.tz(
    workoutSession.startTimeOfFirstExercise,
    gymTimezone,
  );
  const classStarted = startOfExerciseMoment.isBefore(moment().tz(gymTimezone));

  const canStillEditClass = startOfExerciseMoment.isAfter(
    moment()
      .tz(gymTimezone)
      .subtract(minutesToAllowBookingsAfterClassStarted, 'minutes'),
  );

  const showLivestreamOptions = workoutSession.isStreamingWorkout
    && !workoutSession.pairedSessionReference;

  const areaCategory = isHomeBooking ? 'home' : workoutSession.areaCategory ?? 'circuit';

  const shouldOnlyShowSessionTimings = areaCategory !== 'home' && staticMachineAreaZones.includes(areaCategory);

  return (
    <View style={{ flex: 1, backgroundColor: Theme.GREY_FIGMA }}>
      <StatusBarComponent barStyle='light-content' />
      <ScrollView>
        <BookingSummary
          t={t}
          gym={gym}
          isSoloWorkout={false}
          pendingSlot={workoutSession}
          workoutSettings={workoutSettings}
          closeModal={closeModal}
          areaCategory={areaCategory}
        />
        {!UserWorkoutSession.isStreamingWorkoutAndIsHost(workoutSession)
          && !shouldOnlyShowSessionTimings
          && (
            <View style={styles.tile}>
              <Text style={styles.tileHeadingStyle}>{t('typeOfWorkout')}</Text>
              <TypeOfWorkout
                workoutSettings={workoutSettings}
                workoutTypeSelected={onWorkoutTypeSelected}
                areaCategory={workoutSession.areaCategory}
              />
            </View>
          )}
        {showLivestreamOptions
          && (
            <View style={styles.tile}>
              <LivestreamOptionsSummary
                livestreamOptions={workoutSession.livestreamOptions}
                startTimeOfFirstExercise={workoutSession.startTimeOfFirstExercise}
                t={t}
              />
            </View>
          )}
        {!shouldOnlyShowSessionTimings && (
          <View style={styles.tile}>
            <Text style={styles.tileHeadingStyle}>
              {t('yourWorkoutSentenceCase')}
            </Text>
            {isStudioPartnerBooking && (
              <View>
                <UserBookingDisplay
                  t={t}
                  gymTimezone={gymTimezone}
                  userProfile={userProfile}
                  classStarted={classStarted}
                  workoutSession={workoutSession}
                  getUserProfileDetails={userProfileService.getUserProfileDetails}
                />
              </View>
            )}
            <WorkoutSettingsSummary
              t={t}
              gender={userProfile.gender}
              workoutSettings={workoutSettings}
              personaliseWorkout={showCustomiseWorkout}
              showGymEquipment={
                EnabledFeatures.canSelectEquipment(enabledFeatures)
                && isHomeBooking
              }
              showCusomiseWorkoutSettings={showCusomiseWorkoutSettings}
              gym={gym}
            />
          </View>
        )}
        {isHomeGroupClass && (
          <View style={styles.tile}>
            <OpenClassList t={t} workoutId={workoutSession.uniqueReference} />
          </View>
        )}
        {areaCategory !== 'home' && (
          <SessionTimings
            t={t}
            areaCategory={areaCategory}
            startTime={workoutSession.startTimeOfFirstExercise}
            endTime={workoutSession.endTime}
          />
        )}
        {!shouldOnlyShowSessionTimings && (
          <View style={styles.tile}>
            <WorkoutTypeWithBodyGroupText
              t={t}
              gym={gym}
              workoutSettings={workoutSettings}
            />
          </View>
        )}
        {isHomeBooking && !isElite && (
          <View style={styles.tile}>
            <WhatYouNeedForWokoutText
              t={t}
              equipment={workoutSession?.settings?.equipment}
            />
          </View>
        )}
        {isHomeBooking && (
          <View style={styles.tile}>
            <WorkoutFormat t={t} formatType={workoutSession.workoutType} />
          </View>
        )}
      </ScrollView>
      <BookingFlowFooter>
        <View style={styles.footerContainer}>
          <BookingOperationButtons
            t={t}
            size={ButtonSize.MEDIUM}
            gymTimezone={gymTimezone}
            classStarted={classStarted}
            canStillEditClass={canStillEditClass}
            workoutSession={workoutSession}
            changeBooking={changeBooking}
            cancelBooking={cancelBooking}
            changeInvite={changeInvite}
            shareOpenInvite={shareOpenInvite}
            closeModal={closeModal}
            canCheckIn={canCheckIn}
            checkInPressed={checkInPressed}
            checkingInLoading={checkingInLoading}
          />
        </View>
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state: IAppStore): IMapStateProps => ({
  canCheckIn: state.bookings.bookingWizard.onDemandStudioBooking,
  checkingInLoading: state.bookings.bookingWizard.checkInLoading,
});

const mapDispatchProps = (dispatch, ownProps: IProps): IMapDispatchProps => ({
  checkInPressed: () => dispatch(checkInOnDemandBookingPressedAction(ownProps.userProfile)),
});

export default connect(mapStateToProps, mapDispatchProps)(BookingDetails);

const styles = StyleSheet.create({
  tile: {
    backgroundColor: Theme.WHITE,
    marginTop: defaultScale(10),
    paddingHorizontal: defaultScale(18),
    paddingVertical: defaultScale(22),
  },
  tileHeadingStyle: {
    ...Theme.textStyle.heading2,
    marginBottom: defaultScale(18),
  },
  footerContainer: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(18),
    paddingBottom: defaultScale(15 + bottomInset),
  },
  backContainer: {
    flex: 0.3,
    marginRight: defaultScale(5),
  },
  nextContainer: {
    flex: 0.7,
    backgroundColor: Theme.PRIMARY,
  },
});
